/**
 * Template Selector Component
 *
 * Displays available capability templates in a selectable card grid.
 * Shows template name, description, suggested role, and scope count.
 */

import { useState, useEffect } from 'react';
import { CapabilityTemplate, TemplateSuggestedRole } from '../types/api';

interface TemplateSelectorProps {
  workspaceId: string;
  selectedTemplateId: string | null;
  onSelect: (template: CapabilityTemplate | null) => void;
  suggestedRole?: TemplateSuggestedRole;
  disabled?: boolean;
}

const roleColors: Record<TemplateSuggestedRole, string> = {
  admin: 'bg-blue-100 text-blue-700 border-blue-200',
  member: 'bg-gray-100 text-gray-700 border-gray-200',
  viewer: 'bg-gray-100 text-gray-500 border-gray-200',
};

const roleLabels: Record<TemplateSuggestedRole, string> = {
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
};

export function TemplateSelector({
  workspaceId,
  selectedTemplateId,
  onSelect,
  suggestedRole,
  disabled = false,
}: TemplateSelectorProps) {
  const [templates, setTemplates] = useState<CapabilityTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTemplates();
  }, [workspaceId]);

  async function loadTemplates() {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/capability-templates', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      } else {
        setError('Failed to load templates');
      }
    } catch {
      setError('Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  }

  // Filter templates by suggested role if provided
  const filteredTemplates = suggestedRole
    ? templates.filter((t) => t.suggestedRole === suggestedRole)
    : templates;

  // Group templates: suggested for role first, then others
  const sortedTemplates = [...filteredTemplates].sort((a, b) => {
    // System defaults first
    if (a.isSystemDefault && !b.isSystemDefault) return -1;
    if (!a.isSystemDefault && b.isSystemDefault) return 1;
    // Then by display order
    return a.displayOrder - b.displayOrder;
  });

  if (isLoading) {
    return (
      <div className="py-8 text-center">
        <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
        <p className="text-sm text-gray-500 mt-2">Loading templates...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8 text-center">
        <p className="text-sm text-red-600">{error}</p>
        <button
          onClick={loadTemplates}
          className="mt-2 text-sm text-blue-600 hover:text-blue-700"
        >
          Try again
        </button>
      </div>
    );
  }

  if (sortedTemplates.length === 0) {
    return (
      <div className="py-8 text-center text-gray-500">
        <p className="text-sm">No templates available</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* No template option */}
      <button
        type="button"
        onClick={() => onSelect(null)}
        disabled={disabled}
        className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
          selectedTemplateId === null
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-200 hover:border-gray-300'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900">Custom Permissions</p>
            <p className="text-sm text-gray-500 mt-0.5">
              Assign specific scopes manually
            </p>
          </div>
          {selectedTemplateId === null && (
            <svg
              className="w-5 h-5 text-blue-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </div>
      </button>

      {/* Template options */}
      {sortedTemplates.map((template) => (
        <button
          key={template.id}
          type="button"
          onClick={() => onSelect(template)}
          disabled={disabled}
          className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
            selectedTemplateId === template.id
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="font-medium text-gray-900">{template.name}</p>
                <span
                  className={`px-2 py-0.5 text-xs font-medium rounded-full border ${
                    roleColors[template.suggestedRole]
                  }`}
                >
                  {roleLabels[template.suggestedRole]}
                </span>
                {template.isSystemDefault && (
                  <span className="px-2 py-0.5 text-xs font-medium text-purple-700 bg-purple-100 rounded-full">
                    System
                  </span>
                )}
              </div>
              {template.description && (
                <p className="text-sm text-gray-500 mt-1">
                  {template.description}
                </p>
              )}
              <p className="text-xs text-gray-400 mt-2">
                {template.appCount} {template.appCount === 1 ? 'app' : 'apps'} &middot;{' '}
                {template.scopeCount} {template.scopeCount === 1 ? 'permission' : 'permissions'}
              </p>
            </div>
            {selectedTemplateId === template.id && (
              <svg
                className="w-5 h-5 text-blue-600 flex-shrink-0 ml-3"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
        </button>
      ))}
    </div>
  );
}
